import { Card, CardContent, Grid } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError, getAddressCoreAndType } from '../../../utils/abbreviations.util';
import { ProjectType } from '../../../models/project';

const lotTypeOption = [
  {
    value: 'TH',
    label: 'Townhome (TH)',
  },
  {
    value: 'SFR',
    label: 'Single-Family Residential (SFR)',
  },
];

const lotTypeCMOption = [
  {
    value: 'CM',
    label: 'Commercial (CM)',
  },
];

interface BuildingFloorUnitFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  isEnabled?: boolean;
  syncWithDux: any;
  projectTypeValue: any;
  existingData: any;
  projectStreet?: string;
  projectCommunityType?: string;
  floorNo?: number | string;
}

export const BuildingFloorUnitFormWithDefaultValue = ({
  name,
  index,
  onRemove,
  syncWithDux,
  isEnabled = false,
  projectTypeValue,
  existingData,
  projectStreet,
  projectCommunityType,
  floorNo,
}: BuildingFloorUnitFormProps) => {
  const { getValues, setValue, setError, clearErrors } = useFormContext();

  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.generateLotName(getValues('projectID')),
    isEnabled
  );

  const jobsiteRef = useRef<string | null>(null);
  const [jobNumberWarning, setJobNumberWarning] = useState<string | null>(null);
  const [addressAbbrWarning, setAddressAbbrWarning] = useState<string | null>(null);

  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  useWatch({
    name: `${name}.jobsite` || [],
  });

  const watchedAddress = useWatch({
    name: `${name}.address`,
  });

  useEffect(() => {
    if (projectTypeValue && projectTypeValue !== 'RES') {
      setValue(`${name}.lotType`, 'CM');
      if (projectCommunityType === ProjectType.Buildings && !getValues(`${name}.address`)) {
        setValue(`${name}.address`, projectStreet);
      }
      if (floorNo && !getValues(`${name}.unitName`)) {
        setValue(`${name}.unitName`, `${floorNo}-`);
      }
    }
  }, [projectTypeValue]);

  useEffect(() => {
    if (data && data?.data) {
      setValue(`${name}.jobsite`, data?.data?.unitName);
      jobsiteRef.current = data?.data?.unitName;
    }
  }, [data]);

  const handleUnitNameChange = (value: string) => {
    const initialJobsite = jobsiteRef.current || getValues(`${name}.jobsite`);
    const newJobsite = `${initialJobsite}${value}`.trim();
    setValue(`${name}.jobsite`, newJobsite);

    const allJobsites = existingData?.map((u: any) => normalize(u?.jobsite));
    const isJobsiteDuplicate = allJobsites?.includes(normalize(newJobsite));
    if (isJobsiteDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }

    const allUnits = existingData?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.unitName`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      clearErrors(`${name}.unitName`);
    }
  };

  const handleJobnameNameChange = (e: any) => {
    jobsiteRef.current = e;

    const allUnits = existingData?.map((u: any) => normalize(u?.jobsite));

    const isDuplicate = allUnits?.includes(normalize(e));

    if (isDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: string) => {
    const allJobNumbers = existingData?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allJobNumbers?.includes(normalize(value));
    setJobNumberWarning(isDuplicate ? 'Builder Job Number already exists.' : null);
  };

  const handleAddressChange = (value: string) => {
    if (projectCommunityType === ProjectType.Buildings && normalize(value) === normalize(projectStreet ?? '')) {
      clearErrors(`${name}.address`);
      setAddressAbbrWarning(null);
      return;
    }

    const allUnits = existingData?.map((u: any) => normalize(u?.address));
    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
      setAddressAbbrWarning(null);
      return;
    }

    clearErrors(`${name}.address`);

    const { core: currentCore, streetType: currentStreetType } = getAddressCoreAndType(value);

    if (currentStreetType === null || currentCore === '') {
      setAddressAbbrWarning(null);
      return;
    }

    // Exclude self from comparison (unitID may be 0 for new rows, so match by normalized address)
    const otherUnits = existingData?.filter((lot: any) => normalize(lot?.address ?? '') !== normalize(value));
    const isCoreAndTypeMatch = otherUnits?.some((lot: any) => {
      const { core: otherCore, streetType: otherStreetType } = getAddressCoreAndType(lot?.address ?? '');
      return otherCore === currentCore && otherStreetType === currentStreetType;
    });

    setAddressAbbrWarning(
      isCoreAndTypeMatch ? 'Address matches an existing address after abbreviation standardization.' : null
    );
  };

  useEffect(() => {
    if (!watchedAddress) return;
    handleAddressChange(watchedAddress);
  }, [watchedAddress]);

  // const isProjectTypeRES = projectTypeValue === "RES";
  const isProjectTypeRES = true;

  useEffect(() => {
    if (projectTypeValue && projectTypeValue !== 'RES') {
      setValue(`${name}.lotType`, 'CM');
    }
  }, [projectTypeValue]);

  return (
    <Card elevation={0} className="floorUnitBlockWrapper" style={{ marginBottom: '0', background: 'none' }}>
      <CardContent className="  " style={{ paddingTop: '4px', paddingBottom: '4px' }}>
        <Grid container spacing={1}>
          <Grid item lg={10} sm={10} xl={10} xs={10}>
            <div style={{ display: 'none' }}>
              <label>{getValues(`${name}.unitNumber`)}</label>
            </div>
            <Grid container spacing={1}>
              <Grid
                item
                lg={isProjectTypeRES ? 1.8 : 2}
                sm={isProjectTypeRES ? 1.8 : 2}
                xl={isProjectTypeRES ? 1.8 : 2}
                xs={isProjectTypeRES ? 1.8 : 2}
              >
                <FormInput
                  // disabled={true}
                  name={`${name}.jobsite`}
                  onChange={(e: any) => handleJobnameNameChange(e)}
                  label={'Jobsite'}
                  isCustomError={true}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                      getValues('projectID'),
                      // getValues(`${name}.address`),
                      '',
                      getValues(`${name}.unitID`),
                      // getValues(`${name}.unitName`),
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid
                item
                lg={isProjectTypeRES ? 1.7 : 2}
                sm={isProjectTypeRES ? 1.7 : 2}
                xl={isProjectTypeRES ? 1.7 : 2}
                xs={isProjectTypeRES ? 1.7 : 2}
              >
                <FormInput
                  name={`${name}.unitName`}
                  disabled={isLoading}
                  label="Lot/Unit"
                  onChange={(e: any) => handleUnitNameChange(e)}
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  name={`${name}.jobNumber`}
                  label={'Builder Job Number'}
                  onChange={(e: any) => handleJobNumberChange(e)}
                />
                {jobNumberWarning && (
                  <p style={{ color: '#ed6c02', fontSize: '0.75rem', margin: '2px 0 0 0' }}>{jobNumberWarning}</p>
                )}
              </Grid>
              <Grid
                item
                lg={isProjectTypeRES ? 1.5 : 2}
                sm={isProjectTypeRES ? 1.5 : 2}
                xl={isProjectTypeRES ? 1.5 : 2}
                xs={isProjectTypeRES ? 1.5 : 2}
              >
                <FormInput name={`${name}.address`} label="Address" onChange={(e: any) => handleAddressChange(e)} />
                {addressAbbrWarning && (
                  <p style={{ color: '#ed6c02', fontSize: '0.75rem', margin: '2px 0 0 0' }}>{addressAbbrWarning}</p>
                )}
              </Grid>
              <Grid
                item
                lg={isProjectTypeRES ? 1.5 : 2}
                sm={isProjectTypeRES ? 1.5 : 2}
                xl={isProjectTypeRES ? 1.5 : 2}
                xs={isProjectTypeRES ? 1.5 : 2}
              >
                <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormAutoCompleteSelectDynamic
                  name={`${name}.proctorResult`}
                  label="Proctor Result"
                  getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(getValues('projectID'))}
                  optionsValueKey="proctorResultsID"
                  optionsLabelKey="proctorFileName"
                />
              </Grid>
              {isProjectTypeRES && (
                <Grid item lg={1.5} sm={1.5} xl={1.5} xs={1.5}>
                  <FormSelect
                    name={`${name}.lotType`}
                    label="Lot Type"
                    options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                    error={true}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              {/* <FormCheckbox label="Create WO" name={`${name}.createWO`} /> */}
              <FormInput label="Building Permit#" name={`${name}.buildingPermit`} styleField={{ width: '180px' }} />
              {/* <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!syncWithDux} /> */}
              {onRemove &&
                index !== undefined &&
                (getValues(`${name}.disableDelete`) ? (
                  <div style={{ marginLeft: 'auto', flex: '.5' }}>
                    <DeleteButton
                      data={undefined}
                      deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                      onDeleted={() => onRemove(index)}
                    >
                      <>
                        <img src={Icons.Delete} alt="DELETE" />
                      </>
                    </DeleteButton>
                  </div>
                ) : (
                  <div style={{ marginLeft: 'auto', flex: '.5' }}>
                    <DeleteButton
                      data={undefined}
                      deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                      onDeleted={() => onRemove(index)}
                      isDisabled={true}
                    >
                      <>
                        <img src={Icons.DisabledDelete} alt="DELETE" />
                      </>
                    </DeleteButton>
                  </div>
                ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
